import {Dispatch} from "redux";

import {getFavouriteObject} from "@pg-mono/favourites";

import {IRootState} from "../../store/store";
import {hitGtmAddToCompareClick} from "../tracking/favourite_gtm_events";
import {IAmplitudeFavouritesEventParams} from "../tracking/get_amplitude_favourites_event_properties";
import {trackAmplitudeAddToComparison} from "../tracking/track_amplitude_add_to_comparison";
import {trackAmplitudeAddToFavourites} from "../tracking/track_amplitude_add_to_favourites";
import {addRealEstateToFavouritesSuccess} from "../utils/alerts";
import {
    consumePendingComparisonPropertyIds,
    consumePendingFavouriteOfferIntents,
    consumePendingFavouritePropertyIntents,
    IPendingFavouriteOfferIntent,
    IPendingFavouritePropertyIntent,
    PENDING_FAVOURITES_TTL_MS,
    setPendingComparisonPropertyId,
    setPendingFavouriteOfferId,
    setPendingFavouritePropertyId
} from "../utils/pending_favourites_auth";
import {addToComparison, addToFavourites, hydrateFavouritesFromApi} from "./manage_favourites";

const getPendingFavouriteEventParams = (getState: () => IRootState, intent: IPendingFavouritePropertyIntent): IAmplitudeFavouritesEventParams | null => {
    const propertyDetail = getState().property?.property;
    const matchesPage = propertyDetail != null && propertyDetail.id === intent.id;
    const favourite = getState().favourite.favourites.list.find((item) => item.property?.rp_id === intent.id);
    const offerId = intent.offerId ?? (matchesPage ? propertyDetail.offer.id : favourite?.offer?.rp_id);

    if (offerId == null || intent.placement == null) {
        return null;
    }

    return {
        location: intent.location ?? "property_page",
        favouritePlacement: intent.placement,
        offer: {
            id: offerId,
            name: matchesPage ? propertyDetail.offer.name : undefined,
            vendorName: matchesPage ? propertyDetail.offer.vendor.name : undefined,
            address: matchesPage ? propertyDetail.offer.address : undefined
        },
        listContext: intent.listContext,
        property: matchesPage
            ? {
                  id: intent.id,
                  number: propertyDetail.number,
                  area: propertyDetail.area,
                  rooms: propertyDetail.rooms,
                  floor: propertyDetail.floor,
                  price: propertyDetail.price,
                  pricePerSquareMeter: propertyDetail.price_m2
              }
            : {id: intent.id}
    };
};

const getPendingOfferEventParams = (getState: () => IRootState, intent: IPendingFavouriteOfferIntent): IAmplitudeFavouritesEventParams | null => {
    if (intent.placement == null || intent.location == null) {
        return null;
    }

    const offerDetail = getState().offerDetail?.offer;
    const matchesPage = offerDetail != null && offerDetail.id === intent.id;

    return {
        location: intent.location,
        favouritePlacement: intent.placement,
        offer: {
            id: intent.id,
            name: matchesPage ? offerDetail.name : undefined,
            vendorName: matchesPage ? offerDetail.vendor.name : undefined,
            address: matchesPage ? offerDetail.address : undefined
        },
        property: null
    };
};

export const applyPendingFavourites = async (dispatch: Dispatch, getState: () => IRootState) => {
    const propertyIntents = consumePendingFavouritePropertyIntents(PENDING_FAVOURITES_TTL_MS);
    const offerIntents = consumePendingFavouriteOfferIntents(PENDING_FAVOURITES_TTL_MS);
    let didAddFavourite = false;

    for (const intent of propertyIntents) {
        const existingFavourite = getState().favourite.favourites.list.find((favourite) => favourite.property?.rp_id === intent.id);
        const result = await addToFavourites([{...getFavouriteObject({propertyId: intent.id}), ...existingFavourite}], true, true, {
            skipHydrate: true
        })(dispatch, getState);

        if (result instanceof Error) {
            setPendingFavouritePropertyId(intent.id, {
                offerId: intent.offerId,
                placement: intent.placement,
                location: intent.location,
                listContext: intent.listContext
            });
            continue;
        }

        didAddFavourite = true;
        const eventParams = getPendingFavouriteEventParams(getState, intent);

        if (eventParams && existingFavourite == null) {
            trackAmplitudeAddToFavourites(eventParams);
        }
    }

    for (const intent of offerIntents) {
        const existingFavourite = getState().favourite.favourites.list.find((favourite) => favourite.offer?.rp_id === intent.id && !favourite.property?.rp_id);
        const result = await addToFavourites([{...getFavouriteObject({offerId: intent.id}), ...existingFavourite}], true, true, {skipHydrate: true})(
            dispatch,
            getState
        );

        if (result instanceof Error) {
            setPendingFavouriteOfferId(intent.id, {
                placement: intent.placement,
                location: intent.location
            });
            continue;
        }

        didAddFavourite = true;
        const eventParams = getPendingOfferEventParams(getState, intent);

        if (eventParams && existingFavourite == null) {
            trackAmplitudeAddToFavourites(eventParams);
        }
    }

    if (didAddFavourite) {
        await hydrateFavouritesFromApi(dispatch);
        dispatch(addRealEstateToFavouritesSuccess);
    }
};

export const applyPendingComparison = async (dispatch: Dispatch, getState: () => IRootState) => {
    const propertyIds = consumePendingComparisonPropertyIds(PENDING_FAVOURITES_TTL_MS);

    if (!propertyIds.length) {
        return;
    }

    let didAddComparison = false;

    for (const propertyId of propertyIds) {
        const existingFavourite = getState().favourite.favourites.list.find((favourite) => favourite.property?.rp_id === propertyId);
        const result = await addToComparison(
            {
                ...getFavouriteObject({propertyId}),
                ...existingFavourite,
                in_comparison: true
            },
            true,
            {skipHydrate: true}
        )(dispatch, getState);

        if (!result) {
            setPendingComparisonPropertyId(propertyId);
            continue;
        }

        didAddComparison = true;
        const eventParams = getPendingFavouriteEventParams(getState, {
            id: propertyId,
            placement: "rp_favourite_property_compare",
            location: "property_page"
        });

        if (eventParams) {
            trackAmplitudeAddToComparison(eventParams);
        }
    }

    if (didAddComparison) {
        await hydrateFavouritesFromApi(dispatch);
        hitGtmAddToCompareClick(true);
    }
};
import {Dispatch} from "redux";

import {getFavouriteObject} from "@pg-mono/favourites";

import {IRootState} from "../../store/store";
import {hitGtmAddToCompareClick} from "../tracking/favourite_gtm_events";
import {IAmplitudeFavouritesEventParams} from "../tracking/get_amplitude_favourites_event_properties";
import {trackAmplitudeAddToComparison} from "../tracking/track_amplitude_add_to_comparison";
import {trackAmplitudeAddToFavourites} from "../tracking/track_amplitude_add_to_favourites";
import {addRealEstateToFavouritesSuccess} from "../utils/alerts";
import {
    consumePendingComparisonPropertyIds,
    consumePendingFavouriteOfferIntents,
    consumePendingFavouritePropertyIntents,
    IPendingFavouriteOfferIntent,
    IPendingFavouritePropertyIntent,
    PENDING_FAVOURITES_TTL_MS,
    setPendingComparisonPropertyId,
    setPendingFavouriteOfferId,
    setPendingFavouritePropertyId
} from "../utils/pending_favourites_auth";
import {addToComparison, addToFavourites, hydrateFavouritesFromApi} from "./manage_favourites";

const getPendingFavouriteEventParams = (getState: () => IRootState, intent: IPendingFavouritePropertyIntent): IAmplitudeFavouritesEventParams | null => {
    const propertyDetail = getState().property?.property;
    const matchesPage = propertyDetail != null && propertyDetail.id === intent.id;
    const favourite = getState().favourite.favourites.list.find((item) => item.property?.rp_id === intent.id);
    const offerId = intent.offerId ?? (matchesPage ? propertyDetail.offer.id : favourite?.offer?.rp_id);

    if (offerId == null || intent.placement == null) {
        return null;
    }

    return {
        location: intent.location ?? "property_page",
        favouritePlacement: intent.placement,
        offer: {
            id: offerId,
            name: matchesPage ? propertyDetail.offer.name : undefined,
            vendorName: matchesPage ? propertyDetail.offer.vendor.name : undefined,
            address: matchesPage ? propertyDetail.offer.address : undefined
        },
        listContext: intent.listContext,
        property: matchesPage
            ? {
                  id: intent.id,
                  number: propertyDetail.number,
                  area: propertyDetail.area,
                  rooms: propertyDetail.rooms,
                  floor: propertyDetail.floor,
                  price: propertyDetail.price,
                  pricePerSquareMeter: propertyDetail.price_m2
              }
            : {id: intent.id}
    };
};

const getPendingOfferEventParams = (getState: () => IRootState, intent: IPendingFavouriteOfferIntent): IAmplitudeFavouritesEventParams | null => {
    if (intent.placement == null || intent.location == null) {
        return null;
    }

    const offerDetail = getState().offerDetail?.offer;
    const matchesPage = offerDetail != null && offerDetail.id === intent.id;

    return {
        location: intent.location,
        favouritePlacement: intent.placement,
        offer: {
            id: intent.id,
            name: matchesPage ? offerDetail.name : undefined,
            vendorName: matchesPage ? offerDetail.vendor.name : undefined,
            address: matchesPage ? offerDetail.address : undefined
        },
        property: null
    };
};

export const applyPendingFavourites = async (dispatch: Dispatch, getState: () => IRootState) => {
    const propertyIntents = consumePendingFavouritePropertyIntents(PENDING_FAVOURITES_TTL_MS);
    const offerIntents = consumePendingFavouriteOfferIntents(PENDING_FAVOURITES_TTL_MS);
    let didAddFavourite = false;

    for (const intent of propertyIntents) {
        const existingFavourite = getState().favourite.favourites.list.find((favourite) => favourite.property?.rp_id === intent.id);
        const result = await addToFavourites([{...getFavouriteObject({propertyId: intent.id}), ...existingFavourite}], true, true, {
            skipHydrate: true
        })(dispatch, getState);

        if (result instanceof Error) {
            setPendingFavouritePropertyId(intent.id, {
                offerId: intent.offerId,
                placement: intent.placement,
                location: intent.location,
                listContext: intent.listContext
            });
            continue;
        }

        didAddFavourite = true;
        const eventParams = getPendingFavouriteEventParams(getState, intent);

        if (eventParams && existingFavourite == null) {
            trackAmplitudeAddToFavourites(eventParams);
        }
    }

    for (const intent of offerIntents) {
        const existingFavourite = getState().favourite.favourites.list.find((favourite) => favourite.offer?.rp_id === intent.id && !favourite.property?.rp_id);
        const result = await addToFavourites([{...getFavouriteObject({offerId: intent.id}), ...existingFavourite}], true, true, {skipHydrate: true})(
            dispatch,
            getState
        );

        if (result instanceof Error) {
            setPendingFavouriteOfferId(intent.id, {
                placement: intent.placement,
                location: intent.location
            });
            continue;
        }

        didAddFavourite = true;
        const eventParams = getPendingOfferEventParams(getState, intent);

        if (eventParams && existingFavourite == null) {
            trackAmplitudeAddToFavourites(eventParams);
        }
    }

    if (didAddFavourite) {
        await hydrateFavouritesFromApi(dispatch);
        dispatch(addRealEstateToFavouritesSuccess);
    }
};

export const applyPendingComparison = async (dispatch: Dispatch, getState: () => IRootState) => {
    const propertyIds = consumePendingComparisonPropertyIds(PENDING_FAVOURITES_TTL_MS);

    if (!propertyIds.length) {
        return;
    }

    let didAddComparison = false;

    for (const propertyId of propertyIds) {
        const existingFavourite = getState().favourite.favourites.list.find((favourite) => favourite.property?.rp_id === propertyId);
        const result = await addToComparison(
            {
                ...getFavouriteObject({propertyId}),
                ...existingFavourite,
                in_comparison: true
            },
            true,
            {skipHydrate: true}
        )(dispatch, getState);

        if (!result) {
            setPendingComparisonPropertyId(propertyId);
            continue;
        }

        didAddComparison = true;
        const eventParams = getPendingFavouriteEventParams(getState, {
            id: propertyId,
            placement: "rp_favourite_property_compare",
            location: "property_page"
        });

        if (eventParams) {
            trackAmplitudeAddToComparison(eventParams);
        }
    }

    if (didAddComparison) {
        await hydrateFavouritesFromApi(dispatch);
        hitGtmAddToCompareClick(true);
    }
};
